import axios from 'axios'

// Initial state
const state = {
  user: null,
  token: localStorage.getItem('auth_token') || null,
  loading: false,
  error: null
}

// Getters
const getters = {
  isAuthenticated: state => !!state.token && !!state.user,
  currentUser: state => state.user,
  userRole: state => {
    if (!state.user || !state.user.role) return null
    
    // Handle different role formats
    if (typeof state.user.role === 'string') {
      return state.user.role.toLowerCase()
    } else if (typeof state.user.role === 'object') {
      if (state.user.role.name) {
        return state.user.role.name.toLowerCase()
      } else if (state.user.role.id) {
        // Map role IDs to role names
        const roleIdMap = {
          1: 'admin',
          2: 'teller',
          3: 'registrar'
        }
        return roleIdMap[state.user.role.id] || 'unknown'
      }
    }
    
    return 'unknown'
  },
  authLoading: state => state.loading,
  authError: state => state.error
}

// Mutations
const mutations = {
  AUTH_REQUEST(state) {
    state.loading = true
    state.error = null
  },
  
  AUTH_SUCCESS(state, { user, token }) {
    state.loading = false
    state.user = user
    state.token = token
    state.error = null
  },
  
  AUTH_ERROR(state, error) {
    state.loading = false
    state.error = error
  },
  
  LOGOUT(state) {
    state.user = null
    state.token = null
    state.error = null
  },
  
  SET_USER(state, user) {
    state.user = user
  },
  
  CLEAR_ERROR(state) {
    state.error = null
  }
}

// Actions
const actions = {
  // Login action
  async login({ commit, rootGetters }, { username, password }) {
    commit('AUTH_REQUEST')
    
    try {
      console.log('Attempting login with:', { username, password: '****' });
      console.log('API URL:', `${rootGetters.apiBaseUrl}/login`);
      
      // Set the headers
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      };
      
      const response = await axios.post(
        `${rootGetters.apiBaseUrl}/login`, 
        { username, password },
        config
      );
      
      const { user, token } = response.data
      
      // Store token in localStorage
      localStorage.setItem('auth_token', token)
      
      // Set token in axios defaults for future requests
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
      
      commit('AUTH_SUCCESS', { user, token })
      
      return user
    } catch (error) {
      localStorage.removeItem('auth_token')
      
      // Enhanced error logging
      console.error('Login error details:', error);
      
      if (error.response) {
        console.error('API Response Error:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data
        });
        
        // Extract validation errors if available
        const validationErrors = error.response.data.errors;
        if (validationErrors) {
          // Format validation errors into a readable message
          const errorMessages = Object.values(validationErrors)
            .flat()
            .join(', ');
          
          commit('AUTH_ERROR', errorMessages || 'Validation failed');
        } else {
          commit('AUTH_ERROR', error.response.data.message || 'Login failed');
        }
      } else if (error.request) {
        console.error('No response received:', error.request);
        commit('AUTH_ERROR', 'No response from server. Please check your connection.');
      } else {
        console.error('Request setup error:', error.message);
        commit('AUTH_ERROR', error.message || 'Login request failed');
      }
      
      throw error
    }
  },
  
  // Logout action
  async logout({ commit, rootGetters }) {
    try {
      // Call logout API
      await axios.post(`${rootGetters.apiBaseUrl}/logout`, {}, {
        headers: { Authorization: `Bearer ${state.token}` }
      })
    } catch (error) {
      console.error('Logout API error:', error)
    }
    
    // Clear token from localStorage
    localStorage.removeItem('auth_token')
    
    // Remove token from axios defaults
    delete axios.defaults.headers.common['Authorization']
    
    // Reset state
    commit('LOGOUT')
  },
  
  // Get current user data
  async getCurrentUser({ commit, rootGetters }) {
    commit('AUTH_REQUEST')
    
    try {
      // Get the token from state or localStorage
      const token = state.token || localStorage.getItem('auth_token')
      
      if (!token) {
        throw new Error('No authentication token found')
      }
      
      // Set token in axios defaults
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
      
      // Get current user data
      const response = await axios.get(`${rootGetters.apiBaseUrl}/user`)
      
      const user = response.data
      
      commit('AUTH_SUCCESS', { user, token })
      
      return user
    } catch (error) {
      localStorage.removeItem('auth_token')
      delete axios.defaults.headers.common['Authorization']
      
      commit('AUTH_ERROR', error.response?.data?.message || 'Failed to get user data')
      commit('LOGOUT')
      
      throw error
    }
  },
  
  // Clear authentication error
  clearAuthError({ commit }) {
    commit('CLEAR_ERROR')
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
} 